/**
 * A template click. The first time, today's chain from the public viewer:
 * post the copy, poll the import, navigate. The copy is stamped on the record
 * as soon as its shell exists, so a second click opens that copy instead of
 * cloning again. Once the import lands the copy takes the starter's tagline
 * as its description, so its card reads the same line as the starter did. An archived or deleted copy is no copy: the click clones
 * anew, the way the walkthrough starts a new canvas.
 */

import { orgAPI } from "@/api/api";
import { useCopyPublishedCanvas } from "@/api/canvas";
import { ROUTES } from "@/constants/routes";
import Sentry from "@/integrations/Sentry";
import { pollImportedCanvasTerminal } from "@/public-canvas/pollImportedCanvasTerminal";
import { route } from "@/utils/route";
import type { Canvas } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { templateCopy, type HomeRecord } from "./record";
import type { Template } from "./starters";

export const COPY_FAILED = "Couldn't copy the template. Try again.";
/** What the page says while a copy is made, in the order the wait goes. */
export const COPY_LINES: readonly string[] = [
  "Copying the canvas",
  "Bringing in the files",
  "Almost there",
];
/** A copy that already exists only has to open. */
export const OPEN_LINES: readonly string[] = ["Opening the canvas"];

async function canOpen(canvasId: string): Promise<boolean> {
  try {
    const canvas = await orgAPI.canvas.getById(canvasId);
    return !canvas.archived_at;
  } catch {
    return false;
  }
}

export interface TemplateOpen {
  open: (template: Template) => void;
  /** The template being copied or opened, if any. One at a time. */
  pending: string | null;
  /** The status lines for the wait under way, or nothing. */
  lines: readonly string[] | null;
}

export function useTemplateCopy(record: HomeRecord): TemplateOpen {
  const navigate = useNavigate();
  const copyPublished = useCopyPublishedCanvas();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{
    id: string;
    lines: readonly string[];
  } | null>(null);
  const inFlight = useRef(false);
  // The import can take a while; a user who left the home in the meantime is
  // not yanked to the copy when it lands.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { setTemplateCopy } = record;
  const existing = record.record;

  const open = useCallback(
    (template: Template) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const settle = () => {
        inFlight.current = false;
        if (mounted.current) setPending(null);
      };
      const goTo = (canvasId: string) => {
        settle();
        if (mounted.current) {
          navigate(route(ROUTES.CANVAS.DETAIL, { canvasId }));
        }
      };
      const clone = async () => {
        setPending({ id: template.id, lines: COPY_LINES });
        let shell: Canvas;
        try {
          shell = await copyPublished.mutateAsync({ canvasId: template.id });
        } catch (error) {
          Sentry.captureException(error);
          toast.error(COPY_FAILED);
          settle();
          return;
        }
        const shellId = shell.id;
        void setTemplateCopy(template.id, shellId);
        const state = await pollImportedCanvasTerminal(shellId);
        if (state === "failed") {
          void setTemplateCopy(template.id, "");
          toast.error(COPY_FAILED);
          settle();
          return;
        }
        // The update wants the name back; the copy's is the published one's.
        // A description that does not land is not a failed copy.
        try {
          const described = await orgAPI.canvas.update(shellId, {
            name: shell.name,
            description: template.tagline,
          });
          queryClient.setQueryData(["canvas", shellId], described);
        } catch (error) {
          Sentry.captureException(error);
        }
        // A timeout is not a failure: the copy is in the workspace and the
        // canvas page finishes hydrating it.
        goTo(shellId);
      };
      const copy = templateCopy(existing, template.id);
      if (!copy) {
        void clone();
        return;
      }
      setPending({ id: template.id, lines: OPEN_LINES });
      void canOpen(copy).then((openable) => {
        if (openable) goTo(copy);
        else void clone();
      });
    },
    [copyPublished, existing, navigate, queryClient, setTemplateCopy],
  );

  return {
    open,
    pending: pending?.id ?? null,
    lines: pending?.lines ?? null,
  };
}
